import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { sendApiRequest } from '../../utils';
import { toast } from 'react-toastify';
import { FiPrinter, FiArrowLeft, FiUsers } from 'react-icons/fi';

const GeneratePackageReceipt = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState(null);
  const [mlmTree, setMlmTree] = useState(null);
  const [receiptData, setReceiptData] = useState({
    receiptDate: new Date().toISOString().split('T')[0],
    amount: '',
    amountInWords: '',
    paymentMode: 'cash',
    paymentDetails: '',
    remarks: ''
  });

  // Get requestId from query parameters
  const queryParams = new URLSearchParams(location.search);
  const requestId = queryParams.get('requestId');

  useEffect(() => {
    if (requestId) {
      fetchRequestDetails();
    } else {
      setLoading(false);
      toast.error('No request ID provided');
    }
  }, [requestId]);

  const fetchRequestDetails = async () => {
    try {
      setLoading(true);
      // First, fetch all requests
      const response = await sendApiRequest('api/packages/admin/all-requests', {}, 'GET');
      
      if (response.data.success) {
        // Find the specific request by ID
        const foundRequest = response.data.data.find(req => req._id === requestId);
        
        if (foundRequest) {
          // Check if the request is either active or completed
          if (foundRequest.status !== 'active' && foundRequest.status !== 'completed') {
            toast.error('Receipt can only be generated for active or completed requests');
            navigate('/admin/packages/approval');
            return;
          }
          
          setRequest(foundRequest);
          // Pre-fill receipt data with package price
          if (foundRequest.package) {
            setReceiptData(prev => ({
              ...prev,
              amount: foundRequest.package.price.toString(),
              amountInWords: numberToWords(foundRequest.package.price)
            }));
          }
          // Fetch MLM tree
          fetchMlmTree(foundRequest.user._id);
        } else {
          toast.error('Package request not found');
        }
      } else {
        toast.error(response.data.message || 'Failed to fetch package request details');
      }
    } catch (error) {
      console.error('Error fetching package request details:', error);
      toast.error('Failed to fetch package request details');
    } finally {
      setLoading(false);
    }
  };

  const fetchMlmTree = async (userId) => {
    try {
      const response = await sendApiRequest(`api/packages/admin/mlm-tree/${userId}`, {}, 'GET');
      if (response.data.success) {
        setMlmTree(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching MLM tree:', error);
      toast.error('Failed to fetch MLM tree');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setReceiptData(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-generate amount in words when amount changes
    if (name === 'amount' && value) {
      const numericValue = parseFloat(value);
      if (!isNaN(numericValue)) {
        setReceiptData(prev => ({
          ...prev,
          amountInWords: numberToWords(numericValue)
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await sendApiRequest(
        `api/packages/admin/generate-receipt/${requestId}`,
        receiptData,
        'POST'
      );
      
      if (response.data.success) {
        toast.success('Receipt generated successfully');
        // Navigate to the receipt view page
        navigate(`/admin/packages/view-receipt?receiptId=${response.data.data.receiptId}`);
      } else {
        toast.error(response.data.message || 'Failed to generate receipt');
      }
    } catch (error) {
      console.error('Error generating receipt:', error);
      toast.error('Failed to generate receipt');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    navigate('/admin/packages/approval');
  };

  // Function to convert number to words
  const numberToWords = (num) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    
    if (num === 0) return 'Zero';
    
    const convertLessThanThousand = (n) => {
      if (n === 0) return '';
      
      let result = '';
      
      if (n >= 100) {
        result += ones[Math.floor(n / 100)] + ' Hundred ';
        n %= 100;
      }
      
      if (n >= 20) {
        result += tens[Math.floor(n / 10)] + ' ';
        n %= 10;
      } else if (n >= 10) {
        result += teens[n - 10] + ' ';
        n = 0;
      }
      
      if (n > 0) {
        result += ones[n] + ' ';
      }
      
      return result.trim();
    };
    
    let result = '';
    
    if (num >= 10000000) {
      result += convertLessThanThousand(Math.floor(num / 10000000)) + ' Crore ';
      num %= 10000000;
    }
    
    if (num >= 100000) {
      result += convertLessThanThousand(Math.floor(num / 100000)) + ' Lakh ';
      num %= 100000;
    }
    
    if (num >= 1000) {
      result += convertLessThanThousand(Math.floor(num / 1000)) + ' Thousand ';
      num %= 1000;
    }
    
    result += convertLessThanThousand(num);
    
    return result.trim() + ' Rupees Only';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500">Package request not found</p>
        <button 
          onClick={handleBack}
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#49c2c7]/5 via-[#25b2da]/10 to-[#0d61b5]/5 py-8">
      <div className="container mx-auto px-4">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-[#25b2da]/20">
          <div className="bg-gradient-to-r from-[#13407e] to-[#0d61b5] text-white px-6 py-4 flex justify-between items-center">
            <h1 className="text-xl font-semibold">Generate Package Receipt</h1>
            <div className="flex space-x-2">
              <button 
                onClick={handleBack}
                className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
              >
                <FiArrowLeft className="w-5 h-5" />
              </button>
              <button 
                onClick={handlePrint}
                className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
              >
                <FiPrinter className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h2 className="text-lg font-semibold mb-3 text-gray-700">Package Details</h2>
                <div className="space-y-2">
                  <p><span className="font-medium">Package:</span> {request.package?.title || 'N/A'}</p>
                  <p><span className="font-medium">Price:</span> ₹{request.package?.price?.toLocaleString() || 'N/A'}</p>
                  <p><span className="font-medium">Dimensions:</span> {request.package ? `${request.package.width} x ${request.package.length}` : 'N/A'}</p>
                  <p><span className="font-medium">Purchase Date:</span> {new Date(request.purchaseDate).toLocaleDateString()}</p>
                  <p><span className="font-medium">Status:</span> {request.status.charAt(0).toUpperCase() + request.status.slice(1)}</p>
                </div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h2 className="text-lg font-semibold mb-3 text-gray-700">User Details</h2>
                <div className="space-y-2">
                  <p><span className="font-medium">Name:</span> {request.user?.fullName || 'N/A'}</p>
                  <p><span className="font-medium">Email:</span> {request.user?.email || 'N/A'}</p>
                  <p><span className="font-medium">Phone:</span> {request.user?.phone || 'N/A'}</p>
                  <p><span className="font-medium">ID:</span> {request.user?.sponsorCode || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* MLM Tree Section */}
            {mlmTree && (
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <h2 className="text-lg font-semibold mb-3 text-gray-700 flex items-center">
                  <FiUsers className="mr-2" /> MLM Tree
                </h2>
                <div className="space-y-4">
                  {/* Level 1 - User */}
                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <p className="font-medium text-blue-600">{mlmTree.fullName}</p>
                    <p className="text-sm text-gray-600">ID: {mlmTree.sponsorCode}</p>
                  </div>
                  
                  {/* Level 2 - Direct Referrals */}
                  {mlmTree.directReferrals && mlmTree.directReferrals.length > 0 && (
                    <div className="ml-4 border-l-2 border-gray-200 pl-4">
                      <h3 className="text-sm font-medium text-gray-600 mb-2">Direct Referrals</h3>
                      <div className="space-y-2">
                        {mlmTree.directReferrals.map((referral, index) => (
                          <div key={index} className="bg-white p-3 rounded-lg shadow-sm">
                            <p className="font-medium text-green-600">{referral.fullName}</p>
                            <p className="text-sm text-gray-600">ID: {referral.sponsorCode}</p>
                            
                            {/* Level 3 - Referral's Referrals */}
                            {referral.directReferrals && referral.directReferrals.length > 0 && (
                              <div className="ml-4 mt-2 border-l-2 border-gray-200 pl-4">
                                <h4 className="text-sm font-medium text-gray-600 mb-2">Their Referrals</h4>
                                <div className="space-y-2">
                                  {referral.directReferrals.map((subReferral, subIndex) => (
                                    <div key={subIndex} className="bg-white p-2 rounded-lg shadow-sm">
                                      <p className="font-medium text-purple-600">{subReferral.fullName}</p>
                                      <p className="text-sm text-gray-600">ID: {subReferral.sponsorCode}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Receipt Date</label>
                  <input
                    type="date"
                    name="receiptDate"
                    value={receiptData.receiptDate}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹)</label>
                  <input
                    type="number"
                    name="amount"
                    value={receiptData.amount}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount in Words</label>
                  <input
                    type="text"
                    name="amountInWords"
                    value={receiptData.amountInWords}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Mode</label>
                  <select
                    name="paymentMode"
                    value={receiptData.paymentMode}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="upi">UPI</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Details</label>
                  <input
                    type="text"
                    name="paymentDetails"
                    value={receiptData.paymentDetails}
                    onChange={handleInputChange}
                    placeholder="Transaction ID, Cheque No., etc."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  name="remarks"
                  value={receiptData.remarks}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                ></textarea>
              </div>
              
              <div className="flex justify-end">
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Generate Receipt
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneratePackageReceipt; 